import React, {MouseEventHandler, useCallback, useState, ReactNode, useEffect, forwardRef} from 'react';
import classNames from "classnames";

import {IconProps} from "../icons";
import {LoadingIcon} from "../icons/LoadingIcon";


import './button.css';


export type ButtonSize = 'sm' | 'lg' | 'md';
export type ButtonType =  'primary' | 'accent' | 'success' | 'neutral' | 'danger' | 'ai' | 'white';
export type ButtonStyle =  'solid' | 'plain' | 'outlined' | 'soft' | 'inline';

export interface ButtonProps {
    text: ReactNode;
    type?: ButtonType;
    style?: ButtonStyle;
    onClick?: MouseEventHandler;
    Icon?: React.ComponentType<IconProps>;
    className?: string;
    size?: ButtonSize;
    fullWidth?: boolean;
    loading?: boolean
    htmlType?: 'submit' | 'button';
    isDisable?: boolean
    /** Native title attribute — browser tooltip, mainly useful to explain *why*
     *  a button is isDisable'd (isDisable only restyles the button, it doesn't
     *  stop onClick, so the caller's own handler still needs its own guard). */
    title?: string
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>((
    {
        text,
        onClick,
        className,
        Icon,
        type = 'primary',
        style = 'solid',
        size = 'md',
        isDisable = false,
        loading = false,
        fullWidth = false,
        htmlType = 'button',
        title,
    },
    ref
) => {
    const [isDown, setIsDown] = useState<boolean>(false);
    const onDown = useCallback((event: any) => {
        setIsDown(true);
    }, [setIsDown]);

    const handleMouseUp = useCallback(() => {
        setIsDown(false);
    }, [setIsDown]);

    useEffect(() => {
        window.addEventListener('mouseup', handleMouseUp);

        return () => {
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [handleMouseUp])


    let content = <>
        {Icon ? (
            <Icon className="btn__icon" size={size === 'sm' ? 16 : 20}/>
        ) : null}
        <span className='btn__text'>{text}</span>
    </>;

    if (loading) {
        content = (
            <>
                {content}
                <LoadingIcon
                    className='btn__loading'
                    type={type}
                    size={null}
                />
            </>
        )
    }

    return (
        <button
            ref={ref}
            onClick={onClick}
            title={title}
            className={classNames({
                'btn': true,
                'btn--iconOnly': text === null,

                'title-medium': size === 'md' && type !== 'white',
                'body-large': size === 'md' && type === 'white',
                'btn--lg title-medium': size === 'lg',
                'btn--sm title-small': size === 'sm',

                'btn--solid': style === 'solid',
                'btn--plain': style === 'plain',
                'btn--outlined': style === 'outlined',
                'btn--soft': style === 'soft',
                'btn--inline': style === 'inline',

                'btn--primary': type === 'primary' && !isDisable,
                'btn--neutral': type === 'neutral' && !isDisable,
                'btn--danger': type === 'danger' && !isDisable,
                'btn--success': type === 'success' && !isDisable,
                'btn--accent': type === 'accent' && !isDisable,
                'btn--ai' : type === 'ai' && !isDisable,
                'btn--white' : type === 'white' && !isDisable,

                'btn--disabled' : isDisable,
                'btn--down': isDown,
                'btn--fullwidth': fullWidth,
                'btn--clickable': onClick,
                'btn--loading': loading,
                'btn--withIcon': !!Icon,
                [className!]: className,
            })}
            onMouseDown={onDown}
            onMouseLeave={handleMouseUp}
            type={htmlType}
        >
            {content}
        </button>
    )
})